import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const POLL_MS = 8000;
const REMIND_MS = 5 * 60 * 1000;
let audioCtx = null;

export const NOTIFICATION_SOUNDS = [
  { id: "chime", label: "Soft chime", notes: [[880, 0, 0.4], [1318.5, 0.14, 0.55]] },
  { id: "bell", label: "Gentle bell", notes: [[1568, 0, 0.7], [784, 0, 0.5]] },
  { id: "doorbell", label: "Ding-dong", notes: [[659.25, 0, 0.5], [523.25, 0.22, 0.7]] },
  { id: "sparkle", label: "Sparkle", notes: [[1046.5, 0, 0.25], [1318.5, 0.08, 0.25], [1568, 0.16, 0.4]] },
];

function getCtx() {
  audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

function playNotes(notes) {
  try {
    const ctx = getCtx();
    const t = ctx.currentTime;
    notes.forEach(([freq, start, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t + start);
      gain.gain.linearRampToValueAtTime(0.22, t + start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.001, t + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t + start);
      osc.stop(t + start + dur + 0.05);
    });
  } catch { /* audio unavailable */ }
}

export function playNotificationSound(cfg) {
  if (cfg?.sound === "custom" && cfg?.custom_path) {
    const audio = new Audio(`/api/files/${cfg.custom_path}`);
    audio.play().catch(() => {});
    return;
  }
  const def = NOTIFICATION_SOUNDS.find((s) => s.id === cfg?.sound) || NOTIFICATION_SOUNDS[0];
  playNotes(def.notes);
}

export function ensureNotificationPermission() {
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  } catch { /* not supported (e.g. iOS Safari) */ }
}

function showDesktopNotification(title, body) {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const n = new Notification(title, { body, tag: "ejf-new-order", renotify: true });
    n.onclick = () => { try { window.focus(); } catch {} n.close(); };
  } catch { /* not supported */ }
}

export function useOrderNotifier(enabled, soundCfg) {
  const seenLatest = useRef(undefined);
  const lastPing = useRef(0);
  const baseTitle = useRef("");
  const soundRef = useRef(true);
  const cfgRef = useRef(soundCfg);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ejf_admin_sound") !== "off");

  useEffect(() => {
    soundRef.current = soundOn;
    localStorage.setItem("ejf_admin_sound", soundOn ? "on" : "off");
  }, [soundOn]);

  useEffect(() => {
    cfgRef.current = soundCfg;
  }, [soundCfg]);

  useEffect(() => {
    if (!enabled) return undefined;
    const unlock = () => { try { getCtx(); } catch {} ensureNotificationPermission(); };
    window.addEventListener("pointerdown", unlock);
    baseTitle.current = document.title;
    ensureNotificationPermission();

    const ping = () => {
      lastPing.current = Date.now();
      if (soundRef.current) playNotificationSound(cfgRef.current);
    };

    const setTabAlert = (newCount) => {
      document.title = newCount > 0
        ? `(${newCount}) New order${newCount === 1 ? "" : "s"} — Eileen Jenkins Admin`
        : baseTitle.current;
    };

    const check = async () => {
      try {
        const r = await API.get("/admin/orders-latest");
        const { latest, new_count: newCount } = r.data || {};
        setTabAlert(newCount || 0);
        const firstRun = seenLatest.current === undefined;
        const arrived = latest && (firstRun ? false : latest.id !== seenLatest.current);
        if (latest) seenLatest.current = latest.id;
        if (firstRun) lastPing.current = Date.now(); // opening the panel counts as acknowledgement

        if (arrived) {
          const who = latest.customer?.name ? ` · ${latest.customer.name}` : "";
          const amount = `£${Number(latest.total || 0).toFixed(2)}`;
          toast.success(`New order ${latest.order_number}`, {
            description: `${amount}${who}`,
            duration: 10000,
          });
          showDesktopNotification(`New order ${latest.order_number}`, `${amount}${who}`);
          ping();
        } else if (newCount > 0 && Date.now() - lastPing.current >= REMIND_MS) {
          toast.warning(`${newCount} order${newCount === 1 ? "" : "s"} still waiting`, {
            description: "Accept or advance the order to stop the reminders.",
            duration: 10000,
          });
          ping();
        }
      } catch { /* offline or logged out — try again next poll */ }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    return () => {
      clearInterval(timer);
      window.removeEventListener("pointerdown", unlock);
      if (baseTitle.current) document.title = baseTitle.current;
    };
  }, [enabled]);

  return { soundOn, setSoundOn };
}
